/**
 * Client typé pour les routes `/messages` — Phase 4, incrément 4.8
 * (texte) + 4.11 (voicemails audio R2), étendu **offline-first**
 * en Phase 3.3.3 (texte) — l'audio reste à câbler en 3.3.5.
 *
 * **4.8** : 3 méthodes texte (list/create/delete).
 *
 * **4.11** : ajout du flux audio 2-step :
 *   - `requestAudioUploadUrl(homeId, mime, byteSize)` →
 *     `{messageId, mediaKey, uploadUrl, uploadFields}`.
 *   - `uploadAudioToR2(uploadUrl, fields, blob)` — PUT direct R2
 *     via FormData. Ne passe pas par `apiRequest` (URL externe).
 *   - `createMessage` étendu avec un champ `audio` optionnel.
 *   - `uploadVoicemail(homeId, blob)` — helper haut-niveau qui
 *     enchaîne les 3 étapes (allocate URL, PUT, confirm).
 *
 * **3.3.3** : `createMessage` et `deleteMessage` retournent
 * désormais `SendResult<T>` (cf. 3.3.2). Si appelé en offline avec
 * `audio: {...}`, la mutation est enqueued — la confirmation R2
 * HEAD se fera au replay. Tant que le bucket R2 conserve l'objet,
 * ça marche. `requestAudioUploadUrl` reste un POST direct (le
 * presign nécessite forcément le réseau) ; un mode offline complet
 * pour les voicemails sera traité en 3.3.5.
 */
import { apiRequest } from './client.js';
import {
  isNetworkError,
  isOffline,
  sendOrQueueMutation,
  type SendResult,
} from './mutationsReplay.js';
import { enqueueMediaMutation } from '../storage/mutationsQueue.js';
import { deleteBlob, putBlob } from '../storage/blobs.js';
import { bumpPendingCount } from '../stores/mutationsQueueStore.js';
import { showToast } from '../ui/Toast.js';
import { t } from '../i18n/index.js';

export interface ApiMessage {
  id: string;
  homeId: string;
  text: string | null;
  mediaKey: string | null;
  mime: string | null;
  byteSize: number | null;
  /** Presigned GET URL valide 15 min — null si pas d'audio ou R2 down. */
  audioUrl: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AudioConfirmInput {
  messageId: string;
  mediaKey: string;
  mime: string;
  byteSize: number;
}

export interface CreateMessageInput {
  text?: string;
  /** Pré-condition : l'upload R2 a été confirmé (cf. `uploadAudioToR2`). */
  audio?: AudioConfirmInput;
}

export interface UploadUrlResponse {
  messageId: string;
  mediaKey: string;
  uploadUrl: string;
  uploadFields: Record<string, string>;
  expiresIn: number;
}

/**
 * Liste les messages d'un foyer, **DESC par created_at**
 * (newest-first). Chaque message inclut désormais `audioUrl`
 * (presigned 15 min) si audio.
 */
export async function listMessages(homeId: string): Promise<ApiMessage[]> {
  const data = await apiRequest<{ messages: ApiMessage[] }>(`/homes/${homeId}/messages`);
  return data.messages;
}

export async function createMessage(
  homeId: string,
  input: CreateMessageInput,
  localId?: string,
): Promise<SendResult<{ message: ApiMessage }>> {
  return sendOrQueueMutation<{ message: ApiMessage }>({
    method: 'POST',
    path: `/homes/${homeId}/messages`,
    body: input,
    ...(localId ? { localId } : {}),
  });
}

export async function deleteMessage(id: string): Promise<SendResult<void>> {
  return sendOrQueueMutation<void>({
    method: 'DELETE',
    path: `/messages/${id}`,
  });
}

/**
 * Phase 4.11 — alloue un upload R2 pour un voicemail. Le serveur
 * renvoie un messageId UUID pré-réservé qu'on ré-utilise au
 * `createMessage` confirm.
 */
export async function requestAudioUploadUrl(
  homeId: string,
  mime: string,
  byteSize: number,
): Promise<UploadUrlResponse> {
  return apiRequest<UploadUrlResponse>(`/homes/${homeId}/messages/upload-url`, {
    method: 'POST',
    body: { mime, byteSize },
  });
}

/**
 * Upload direct du blob vers R2 via le presigned POST. Bypass
 * `apiRequest` car l'URL est externe (R2 endpoint).
 *
 * Retourne `true` si l'upload a réussi (R2 répond 200-299), false
 * sinon — l'appelant ne doit PAS appeler `createMessage` avec
 * `audio` si le upload a échoué (le HEAD R2 côté confirm
 * échouerait, mais autant éviter le round-trip).
 */
export async function uploadAudioToR2(
  uploadUrl: string,
  fields: Record<string, string>,
  blob: Blob,
): Promise<boolean> {
  const form = new FormData();
  for (const [k, v] of Object.entries(fields)) {
    form.append(k, v);
  }
  form.append('file', blob);
  const res = await fetch(uploadUrl, {
    method: 'POST',
    body: form,
  });
  return res.ok;
}

/**
 * Helper haut-niveau : alloue URL → PUT R2 → confirm. Retourne le
 * `SendResult` du confirm.
 *
 * **Phase 3.3.5 — offline-first complet** :
 *
 *   - Le blob est **persisté en IDB d'abord** (sous `voice:<localId>`).
 *   - Si on est offline ou si une étape réseau échoue (presign, PUT R2,
 *     confirm), on enqueue une **media-mutation** que le replay engine
 *     ré-exécutera à la reconnexion (flow 3-step recommencé à zéro avec
 *     un presign frais, cf. `replayMediaMutation`).
 *   - Si tout réussit en ligne, le blob IDB est purgé immédiatement
 *     (R2 + DB serveur en ont la copie).
 *
 * `localId` est un UUID v4 généré côté client — utilisé uniquement
 * pour la clé IDB du blob (`voice:<localId>`). L'id serveur du message
 * sera attribué par `requestAudioUploadUrl` (ou par le replay si la
 * mutation est queued). C'est `messages-sync` qui réconcilie au
 * prochain GET refresh.
 *
 * Note : `text` peut être omis (audio-only) ou fourni (texte + audio).
 */
export async function uploadVoicemail(
  homeId: string,
  blob: Blob,
  options: { text?: string } = {},
  /** Phase 3.3.7 — id local du store domain pour le remap au replay. */
  storeLocalId?: string,
): Promise<SendResult<{ message: ApiMessage }>> {
  const mime = blob.type || 'audio/webm';

  // 1. Persiste le blob en IDB AVANT toute tentative réseau. Le blob
  //    survit à un crash entre les étapes — le replay peut toujours
  //    le retrouver.
  const internalLocalId = generateLocalId();
  const blobKey = `voice:${internalLocalId}`;
  await putBlob(blobKey, blob);

  // 2. Tentative en ligne — flow 3-step direct si tout réussit.
  if (!isOffline()) {
    try {
      const upload = await requestAudioUploadUrl(homeId, mime, blob.size);
      const ok = await uploadAudioToR2(upload.uploadUrl, upload.uploadFields, blob);
      if (ok) {
        // Phase 3.3.7 : passe storeLocalId à `createMessage` pour
        // l'émission du remap event au success.
        const result = await createMessage(
          homeId,
          {
            ...(options.text ? { text: options.text } : {}),
            audio: {
              messageId: upload.messageId,
              mediaKey: upload.mediaKey,
              mime,
              byteSize: blob.size,
            },
          },
          storeLocalId,
        );
        if (result.kind === 'sent') {
          // R2 et serveur ont la copie ; on libère IDB local.
          await deleteBlob(blobKey);
        }
        return result;
      }
      // R2 PUT a échoué (non-2xx) — typiquement presigned expiré ou
      // bucket down. Traite comme un network err → on enqueue.
    } catch (err) {
      if (!isNetworkError(err)) {
        // Server error (ApiError) — pas d'enqueue, rethrow. On purge
        // aussi le blob (le serveur a rejeté, garder le blob n'a pas
        // de sens : l'utilisateur verra l'erreur côté UI).
        await deleteBlob(blobKey);
        throw err;
      }
      // Network err — fall-through vers l'enqueue ci-dessous.
    }
  }

  // 3. Offline ou network failure → enqueue media-mutation pour replay.
  const seq = await enqueueMediaMutation(
    {
      blobKey,
      mime,
      byteSize: blob.size,
      uploadUrlPath: `/homes/${homeId}/messages/upload-url`,
      confirmPath: `/homes/${homeId}/messages`,
      confirmKind: 'voicemail',
      confirmExtras: options.text ? { text: options.text } : {},
    },
    storeLocalId,
  );
  notifyVoicemailQueued();
  return { kind: 'queued', seq };
}

/**
 * UUID v4 généré côté client pour la clé IDB locale du blob. Utilise
 * `crypto.randomUUID` si dispo (tous navigateurs modernes) ; fallback
 * pseudo-aléatoire pour les environnements de test legacy.
 */
function generateLocalId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback simple — pas crypto-secure mais suffisant pour une clé
  // IDB locale (pas de collision avec ids serveur, qui sont des UUIDs
  // crypto-securisés).
  return `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Effet de bord UI à l'enqueue d'un voicemail — bump le store +
 * toast i18n, cohérent avec `notifyQueued` de `mutationsReplay`.
 * Try/catch défensif : la mutation reste persistée même si le
 * toast panique (DOM absent en test, etc.).
 */
function notifyVoicemailQueued(): void {
  try {
    bumpPendingCount();
    showToast({ message: t('mutations_queued'), kind: 'info' });
  } catch (err) {
    console.warn('[messages] notifyVoicemailQueued side-effect failed:', err);
  }
}
